import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import type { DriverListItem } from '@kangaroo/shared';

import { requestTeamJoin } from '../../features/teams';

function getErrorMessage(error: unknown, fallback = 'Unable to send join request') {
  if (typeof error === 'object' && error !== null && 'response' in error) {
    const response = (error as { response?: { data?: { message?: string } } }).response;
    if (response?.data?.message) {
      return response.data.message;
    }
  }

  return error instanceof Error ? error.message : fallback;
}

type FamilyTeamJoinCardProps = {
  linkedDrivers: DriverListItem[];
  assignedKartLabelByDriverId?: Record<string, string>;
  onRequested: (teamName: string) => void;
};

export function FamilyTeamJoinCard({
  linkedDrivers,
  assignedKartLabelByDriverId = {},
  onRequested,
}: FamilyTeamJoinCardProps) {
  const [teamCode, setTeamCode] = useState('');
  const [selectedDriverIds, setSelectedDriverIds] = useState<string[]>([]);
  const [bringsOwnKartByDriverId, setBringsOwnKartByDriverId] = useState<Record<string, boolean>>({});
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const allSelected = linkedDrivers.length > 0 && selectedDriverIds.length === linkedDrivers.length;

  const selectedCountLabel = selectedDriverIds.length === 1 ? '1 driver selected' : `${selectedDriverIds.length} drivers selected`;

  function toggleDriver(driverId: string) {
    setSelectedDriverIds((current) => {
      if (current.includes(driverId)) {
        setBringsOwnKartByDriverId((kartFlags) => {
          const next = { ...kartFlags };
          delete next[driverId];
          return next;
        });
        return current.filter((id) => id !== driverId);
      }

      if (assignedKartLabelByDriverId[driverId]) {
        setBringsOwnKartByDriverId((kartFlags) => ({ ...kartFlags, [driverId]: true }));
      }

      return [...current, driverId];
    });
    setError(null);
    setSuccessMessage(null);
  }

  function toggleAllDrivers() {
    if (allSelected) {
      setSelectedDriverIds([]);
      setBringsOwnKartByDriverId({});
    } else {
      setSelectedDriverIds(linkedDrivers.map((driver) => driver.id));
      setBringsOwnKartByDriverId(Object.fromEntries(
        linkedDrivers
          .filter((driver) => assignedKartLabelByDriverId[driver.id])
          .map((driver) => [driver.id, true]),
      ));
    }
    setError(null);
    setSuccessMessage(null);
  }

  function toggleBringsOwnKart(driverId: string, checked: boolean) {
    setBringsOwnKartByDriverId((current) => ({ ...current, [driverId]: checked }));
    setError(null);
    setSuccessMessage(null);
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setSuccessMessage(null);

    if (teamCode.trim().length < 2) {
      setError('Enter the club team code.');
      return;
    }

    if (selectedDriverIds.length === 0) {
      setError('Select at least one driver to register with the team.');
      return;
    }

    setSubmitting(true);

    try {
      const response = await requestTeamJoin({
        teamCode: teamCode.trim().toUpperCase(),
        drivers: selectedDriverIds.map((driverId) => ({
          driverId,
          bringsOwnKart: bringsOwnKartByDriverId[driverId] ?? false,
        })),
      });
      setSuccessMessage(`Request sent to ${response.team.name}. A team owner or coach can accept it from Invitations.`);
      onRequested(response.team.name);
    } catch (submitError) {
      setError(getErrorMessage(submitError));
    } finally {
      setSubmitting(false);
    }
  }

  if (linkedDrivers.length === 0) {
    return (
      <Box sx={{ px: 2.5, py: 2 }}>
        <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', lineHeight: 1.55 }}>
          Add a child driver to your family team before requesting to join a club team.
        </Typography>
      </Box>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ px: 2.5, py: 2 }}>
      <Stack spacing={2}>
        <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', lineHeight: 1.55 }}>
          Send selected drivers to an existing club team. Their sessions move with them, and assigned family karts can
          join the club fleet for service and setup.
        </Typography>

        {error ? <Alert severity="error">{error}</Alert> : null}
        {successMessage ? <Alert severity="success">{successMessage}</Alert> : null}

        <TextField
          label="Club team code"
          value={teamCode}
          onChange={(event) => {
            setTeamCode(event.target.value.toUpperCase());
            setError(null);
            setSuccessMessage(null);
          }}
          placeholder="AVR24"
          size="small"
          fullWidth
          disabled={submitting || Boolean(successMessage)}
        />

        <Box>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: 'text.secondary' }}>
              Drivers to register
            </Typography>
            <Button size="small" onClick={toggleAllDrivers} sx={{ textTransform: 'none', fontSize: '0.72rem' }}>
              {allSelected ? 'Clear all' : 'Select all'}
            </Button>
          </Stack>
          <Stack spacing={1}>
            {linkedDrivers.map((driver) => {
              const selected = selectedDriverIds.includes(driver.id);
              const kartLabel = assignedKartLabelByDriverId[driver.id];

              return (
                <Box
                  key={driver.id}
                  sx={{
                    border: '1px solid',
                    borderColor: selected ? 'primary.main' : 'divider',
                    bgcolor: selected ? 'rgba(124,58,237,0.08)' : 'transparent',
                    borderRadius: 1.25,
                    px: 1.25,
                    py: 1,
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    <Checkbox
                      checked={selected}
                      onChange={() => toggleDriver(driver.id)}
                      size="small"
                      disabled={submitting || Boolean(successMessage)}
                      sx={{ p: 0.25, mt: 0.1 }}
                    />
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Stack direction="row" spacing={0.75} alignItems="center" useFlexGap flexWrap="wrap">
                        <Typography sx={{ fontSize: '0.88rem', fontWeight: 800, lineHeight: 1.25 }}>
                          {driver.name}{driver.driverNumber ? ` · #${driver.driverNumber}` : ''}
                        </Typography>
                        {kartLabel ? <Chip size="small" label={`Kart ${kartLabel}`} sx={{ height: 20, fontSize: '0.66rem', fontWeight: 800 }} /> : null}
                      </Stack>
                      {selected && kartLabel ? (
                        <Stack direction="row" spacing={0.75} sx={{ mt: 1 }}>
                          <Button
                            size="small"
                            variant={bringsOwnKartByDriverId[driver.id] ?? false ? 'contained' : 'outlined'}
                            onClick={() => toggleBringsOwnKart(driver.id, true)}
                            disabled={submitting || Boolean(successMessage)}
                            sx={{ textTransform: 'none', fontSize: '0.7rem', fontWeight: 800, px: 1, py: 0.25, minWidth: 0 }}
                          >
                            Bring kart
                          </Button>
                          <Button
                            size="small"
                            variant={bringsOwnKartByDriverId[driver.id] ?? false ? 'outlined' : 'contained'}
                            onClick={() => toggleBringsOwnKart(driver.id, false)}
                            disabled={submitting || Boolean(successMessage)}
                            sx={{ textTransform: 'none', fontSize: '0.7rem', fontWeight: 800, px: 1, py: 0.25, minWidth: 0 }}
                          >
                            Use club kart
                          </Button>
                        </Stack>
                      ) : null}
                      {selected && !kartLabel ? (
                        <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary', mt: 0.65, lineHeight: 1.35 }}>
                          No family kart assigned. They&apos;ll use the club fleet.
                        </Typography>
                      ) : null}
                    </Box>
                  </Stack>
                </Box>
              );
            })}
          </Stack>
          {selectedDriverIds.length > 0 ? (
            <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary', mt: 1 }}>
              {selectedCountLabel}
            </Typography>
          ) : null}
        </Box>

        <Button
          type="submit"
          variant="contained"
          disabled={submitting || Boolean(successMessage)}
          sx={{ alignSelf: 'flex-start', textTransform: 'none', fontWeight: 800 }}
        >
          {submitting ? 'Sending…' : 'Request club team access'}
        </Button>
      </Stack>
    </Box>
  );
}
